import { DocumentSearchParams } from '../../types/models'

interface DocumentFiltersProps {
  filters: DocumentSearchParams
  onFiltersChange: (filters: DocumentSearchParams) => void
  onReset: () => void
  /** フィルタ行の右端に表示する総件数 */
  totalCount?: number
}

export function DocumentFilters({ filters, onFiltersChange, onReset, totalCount }: DocumentFiltersProps) {

  const statusOptions = [
    { value: '', label: 'すべて' },
    { value: 'draft', label: 'ドラフト' },
    { value: 'processing', label: '処理中' },
    { value: 'completed', label: '完了' },
    { value: 'failed', label: 'エラー' }
  ]

  const sortOptions = [
    { value: 'createdAt', label: '作成日時' },
    { value: 'updatedAt', label: '更新日時' },
    { value: 'title', label: 'タイトル' }
  ]

  const handleFilterChange = (key: keyof DocumentSearchParams, value: any) => {
    onFiltersChange({
      ...filters,
      [key]: value,
      page: 1 // フィルタ変更時はページをリセット
    })
  }

  const handleDateChange = (type: 'from' | 'to', value: string) => {
    const date = value ? new Date(value) : undefined
    handleFilterChange(type, date)
  }

  const formatDateForInput = (date: Date | undefined) => {
    if (!date) return ''
    return date.toISOString().split('T')[0]
  }

  const hasActiveFilters = Boolean(
    filters.query ||
    filters.status ||
    filters.excludeOwn ||
    filters.ownOnly ||
    filters.from ||
    filters.to ||
    (filters.participants && filters.participants.length > 0)
  )

  return (
    <div className="toolbar">
      <div className="toolbar-left flex-wrap">
        <div className="search-bar" style={{ width: 'auto', flex: '1 1 180px', minWidth: 160, maxWidth: 320 }}>
          <svg fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
            <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder="議事録を検索..."
            value={filters.query || ''}
            onChange={(e) => handleFilterChange('query', e.target.value)}
          />
        </div>

        {/* ステータス絞り込み（モバイルでは非表示） */}
        <select
          value={filters.status || ''}
          onChange={(e) => handleFilterChange('status', e.target.value || undefined)}
          className="form-select hide-mobile"
          style={{ width: 'auto' }}
        >
          {statusOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        {/* 並び順 */}
        <select
          value={filters.sortBy === 'updatedAt' ? 'updatedAt' : 'createdAt'}
          onChange={(e) => onFiltersChange({ ...filters, sortBy: e.target.value as 'createdAt' | 'updatedAt', sortOrder: 'desc' })}
          className="form-select"
          style={{ width: 'auto' }}
          title="並び順"
        >
          <option value="createdAt">作成日時順</option>
          <option value="updatedAt">更新日時順</option>
        </select>

        {/* 作成者フィルタ */}
        <div className="flex items-center gap-2">
          <label className="filter-label whitespace-nowrap">作成者</label>
          <select
            value={filters.ownOnly ? 'me' : filters.excludeOwn ? 'others' : 'all'}
            onChange={(e) => {
              const v = e.target.value
              onFiltersChange({
                ...filters,
                ownOnly: v === 'me' ? true : undefined,
                excludeOwn: v === 'others' ? true : undefined,
                page: 1
              })
            }}
            className="form-select"
            style={{ width: 'auto' }}
          >
            <option value="all">全員</option>
            <option value="me">自分</option>
            <option value="others">自分以外</option>
          </select>
        </div>

        {/* 日付フィルタ */}
        <div className="flex items-center gap-2">
          <label className="filter-label whitespace-nowrap">期間</label>
          <input
            type="date"
            value={formatDateForInput(filters.from)}
            onChange={(e) => handleDateChange('from', e.target.value)}
            className="form-input"
            style={{ width: 'auto' }}
          />
          <span className="text-tertiary">〜</span>
          <input
            type="date"
            value={formatDateForInput(filters.to)}
            onChange={(e) => handleDateChange('to', e.target.value)}
            className="form-input"
            style={{ width: 'auto' }}
          />
        </div>
      </div>

      <div className="toolbar-right">
        {hasActiveFilters && (
          <button
            onClick={onReset}
            className="btn btn-ghost btn-sm"
          >
            クリア
          </button>
        )}
        {typeof totalCount === 'number' && (
          <span className="text-sm text-secondary" style={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
            {totalCount}件の議事録
          </span>
        )}
      </div>
    </div>
  )
}